package com.partejar.app.core.ui.components

import androidx.compose.foundation.gestures.detectDragGesturesAfterLongPress
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.ime
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme

/** A locally positioned add action that remains in the visible area above the dock and IME. */
@Composable
fun PartejarDraggableFab(
    onClick: () -> Unit,
    dockClearance: Dp,
    contentDescription: String,
    modifier: Modifier = Modifier
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    val focusManager = LocalFocusManager.current
    val layoutDirection = LocalLayoutDirection.current
    val safePadding = WindowInsets.safeDrawing.asPaddingValues()
    val imeBottom = WindowInsets.ime.asPaddingValues().calculateBottomPadding()
    val fabSize = dimensions.controlHeight
    var requestedOffset by remember { mutableStateOf<DpOffset?>(null) }

    BoxWithConstraints(modifier = modifier.fillMaxSize()) {
        val minX = safePadding.calculateLeftPadding(layoutDirection) + spacing.space16
        val maxX = (maxWidth - safePadding.calculateRightPadding(layoutDirection) - fabSize - spacing.space16)
            .coerceAtLeast(minX)
        val minY = safePadding.calculateTopPadding() + spacing.space16
        val protectedBottom = maxOf(dockClearance, imeBottom)
        val maxY = (maxHeight - protectedBottom - fabSize - spacing.space16).coerceAtLeast(minY)
        val preferred = requestedOffset ?: DpOffset(maxX, maxY)
        val offset = DpOffset(
            x = preferred.x.coerceIn(minX, maxX),
            y = preferred.y.coerceIn(minY, maxY)
        )

        Surface(
            onClick = {
                focusManager.clearFocus()
                onClick()
            },
            modifier = Modifier
                .align(Alignment.TopStart)
                .offset(offset.x, offset.y)
                .size(fabSize)
                .pointerInput(minX, maxX, minY, maxY) {
                    detectDragGesturesAfterLongPress(
                        onDrag = { change, dragAmount ->
                            change.consume()
                            val currentOffset = requestedOffset ?: offset
                            requestedOffset = DpOffset(
                                x = (currentOffset.x + dragAmount.x.toDp()).coerceIn(minX, maxX),
                                y = (currentOffset.y + dragAmount.y.toDp()).coerceIn(minY, maxY)
                            )
                        }
                    )
                },
            shape = MaterialTheme.shapes.large,
            color = MaterialTheme.colorScheme.primaryContainer,
            contentColor = MaterialTheme.colorScheme.onPrimaryContainer,
            shadowElevation = dimensions.floatingDockElevation
        ) {
            androidx.compose.foundation.layout.Box(contentAlignment = Alignment.Center) {
                Icon(
                    imageVector = PartejarIcons.Actions.add,
                    contentDescription = contentDescription,
                    modifier = Modifier.size(dimensions.iconDefault)
                )
            }
        }
    }
}

private fun Float.toDp(): Dp = dp
